export interface ServiceData {
  slug: string;
  title: string;
  subtitle: string;
  price: string;
  image: string;
  description: string;
  features: string[];
  process: { step: string; desc: string }[];
}

export const services: ServiceData[] = [
  {
    slug: "interior-painting",
    title: "Interior Painting",
    subtitle: "Clean lines and flawless finishes for every room in your home",
    price: "Free Estimate",
    image: "/images/services/interior-painting.webp",
    description:
      "Give the inside of your home a fresh, polished look with professional interior painting. From single accent walls to whole-home repaints, we handle careful surface prep, crisp cut-ins, and smooth, even coats that hold up to daily life. We work clean, protect your furniture and floors, and leave every room ready to enjoy.",
    features: [
      "Walls, ceilings, doors, and trim",
      "Careful patching and surface preparation",
      "Premium low-odor interior paints",
      "Crisp, clean lines on edges and trim",
      "Furniture and floor protection throughout",
      "Full cleanup and walkthrough at the end",
    ],
    process: [
      { step: "Consultation", desc: "We walk the rooms with you, discuss colors and finishes, and provide a clear written estimate." },
      { step: "Preparation", desc: "Furniture is covered, floors are protected, and walls are patched, sanded, and primed as needed." },
      { step: "Painting", desc: "Even, professional coats with sharp cut-ins around trim, ceilings, and fixtures." },
      { step: "Final Walkthrough", desc: "We inspect every wall with you, touch up anything needed, and leave the space clean." },
    ],
  },
  {
    slug: "exterior-painting",
    title: "Exterior Painting",
    subtitle: "Weather-resistant curb appeal built for the Florida sun",
    price: "Free Estimate",
    image: "/images/services/exterior-painting.webp",
    description:
      "Florida heat, rain, and humidity are tough on exterior paint. Our exterior painting service starts with thorough pressure-cleaning and prep, then applies quality exterior-grade coatings that resist fading, peeling, and mildew — so your home looks sharp and stays protected season after season.",
    features: [
      "Stucco, siding, and trim painting",
      "Pressure cleaning before painting",
      "Caulking and crack sealing included",
      "UV- and mildew-resistant exterior paints",
      "Careful protection of landscaping and walkways",
      "Even, long-lasting coverage on every surface",
    ],
    process: [
      { step: "Inspection", desc: "We assess the condition of your stucco, siding, and trim and recommend the right coating system." },
      { step: "Prep & Cleaning", desc: "Pressure washing, scraping, caulking, and priming so the new paint bonds properly." },
      { step: "Painting", desc: "Quality exterior paint applied evenly, with careful attention to edges and details." },
      { step: "Final Review", desc: "A full walk-around with you to confirm every surface is covered and clean." },
    ],
  },
  {
    slug: "tile-vinyl-flooring",
    title: "Tile & Vinyl Floor Installation",
    subtitle: "Ceramic tile and luxury vinyl installed level, tight, and true",
    price: "Free Estimate",
    image: "/images/services/tile-vinyl-flooring.webp",
    description:
      "Whether you want the classic durability of ceramic tile or the warmth and easy care of vinyl plank, we install floors that look right and last. We prepare the subfloor properly, lay out patterns carefully, and finish with clean transitions and tight grout lines — no lippage, no hollow spots, no shortcuts.",
    features: [
      "Ceramic and porcelain tile installation",
      "Luxury vinyl plank and sheet vinyl",
      "Subfloor preparation and leveling",
      "Precise layout, cuts, and transitions",
      "Clean, even grout lines and sealing",
      "Old flooring removal available",
    ],
    process: [
      { step: "Measure & Quote", desc: "We measure the space, review material options with you, and provide a clear estimate." },
      { step: "Subfloor Prep", desc: "Old flooring is removed and the subfloor is cleaned, repaired, and leveled." },
      { step: "Installation", desc: "Tile or vinyl is laid with precise spacing, straight lines, and clean cuts at every edge." },
      { step: "Finishing", desc: "Grouting, sealing, transitions, and baseboards — then a final inspection with you." },
    ],
  },
  {
    slug: "bathroom-remodeling",
    title: "Bathroom Remodeling",
    subtitle: "Refresh or transform your bathroom with quality craftsmanship",
    price: "Free Estimate",
    image: "/images/services/bathroom-remodeling.webp",
    description:
      "From simple refreshes to full renovations, we remodel bathrooms with the same care we'd put into our own home. Tile work, vanities, fixtures, lighting, and paint all come together under one team — coordinated properly, finished cleanly, and built to handle daily use for years.",
    features: [
      "Shower and tub surround tile work",
      "Vanity and countertop installation",
      "Fixture and faucet replacement",
      "Flooring, paint, and trim work",
      "Lighting and hardware updates",
      "One team from demo to final detail",
    ],
    process: [
      { step: "Design & Estimate", desc: "We discuss your vision and budget, then lay out a realistic scope and timeline." },
      { step: "Demo & Prep", desc: "Careful removal of old materials with the rest of your home protected from dust." },
      { step: "Build & Install", desc: "Tile, vanity, fixtures, paint, and trim installed in the right order, done right." },
      { step: "Final Walkthrough", desc: "We check every joint, seal, and finish with you before calling the job complete." },
    ],
  },
  {
    slug: "drywall-repair",
    title: "Drywall Repair",
    subtitle: "Seamless patches and smooth walls — like the damage never happened",
    price: "Free Estimate",
    image: "/images/services/drywall-repair.webp",
    description:
      "Holes, cracks, water damage, or dents — we repair drywall so the fix disappears. Every repair is properly taped, mudded, sanded, and texture-matched to blend with the surrounding wall, then primed and ready for paint. Small patches or full panels, the result is a smooth, uniform surface.",
    features: [
      "Hole and crack repair of any size",
      "Water damage repair and replacement",
      "Professional taping and mudding",
      "Texture matching to existing walls",
      "Smooth sanding and priming",
      "Paint-ready or fully painted finish",
    ],
    process: [
      { step: "Assessment", desc: "We inspect the damage, identify any underlying issues, and quote the repair clearly." },
      { step: "Repair", desc: "Damaged sections are cut out and replaced, or patched and reinforced as needed." },
      { step: "Finish Work", desc: "Taping, mudding, sanding, and texture matching until the repair blends invisibly." },
      { step: "Prime & Paint", desc: "The repaired area is primed and painted to match — the fix simply disappears." },
    ],
  },
  {
    slug: "cabinet-restoration-installation",
    title: "Cabinet Restoration & Installation",
    subtitle: "Kitchen cabinet painting, refinishing, and new cabinet installs",
    price: "Free Estimate",
    image: "/images/services/cabinet-restoration-installation.webp",
    description:
      "Transform your kitchen without the cost of a full remodel. We restore existing cabinets with professional cleaning, prep, and durable factory-smooth painted finishes — or install brand-new cabinets when it's time for a change. Either way, you get a kitchen that feels new at a price that makes sense.",
    features: [
      "Cabinet painting and refinishing",
      "Durable, smooth sprayed finishes",
      "New cabinet installation",
      "Hardware replacement and alignment",
      "Doors, drawers, and frames included",
      "Kitchens, bathrooms, and laundry rooms",
    ],
    process: [
      { step: "Evaluation", desc: "We assess your cabinets and help you decide between restoration and replacement." },
      { step: "Prep Work", desc: "Doors and hardware removed, surfaces degreased, sanded, and primed for adhesion." },
      { step: "Finish or Install", desc: "Cabinets are painted with a smooth, durable finish — or new units are set level and secure." },
      { step: "Reassembly", desc: "Doors, drawers, and hardware aligned and adjusted, then a final quality check." },
    ],
  },
];
